import React from 'react'
import { Button } from '@/components/ui/button'
import { AlertDialog, AlertDialogContent, AlertDialogTrigger, AlertDialogCancel, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import { useTRPC } from '@/trpc'
import { TRPCClientError } from '@trpc/client'
import { TContactUpdate } from '@/types/trpc.types'
import * as Icons from 'lucide-react'
import MultiSelect from '@/components/ui/multi-select'

interface Props {
    contact_id: string;
    defaultValues?: Partial<UpdateContactSchema>
    trigger?: React.ReactNode
    onUpdated?: (updatedContact:TContactUpdate) => void
}

const updateContactSchema = z.object({
    contact_id: z.string().uuid(),
    contact_name: z.string().min(1, "Naam is verplicht"),
    email: z.string().optional(),
    telephone: z.string().optional(),
    contact_via: z.string().array().optional(), // email, telephone, whatsapp, other
    note: z.string().optional(),
    reliability: z.number().min(0).max(5).optional(),
    quality_players: z.number().min(0).max(5).optional(),
    company_name: z.string().optional(),
})
export type UpdateContactSchema = z.infer<typeof updateContactSchema>;

const UpdateContact = ({ trigger, contact_id, defaultValues, onUpdated }: Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);

    const form = useForm<UpdateContactSchema>({
        resolver: zodResolver(updateContactSchema),
        defaultValues: {
            contact_id: contact_id,
            ...defaultValues
        }
    })
    const formValues = form.watch()
    const isFormValid = updateContactSchema.safeParse(formValues).success;

    const updateMutation = useMutation({
        mutationFn: async (data:UpdateContactSchema) => {
            const updatedContact = await trpc.contacts.update.mutate(data)
            return updatedContact as TContactUpdate
        },
        onSuccess: (data) => {
            onUpdated?.(data);
            toast.success("Contact succesvol gewijzigd");
            setOpen(false);
            form.reset();
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    const handleSubmit = () => {
        const values = form.getValues();
        const isValid = updateContactSchema.safeParse(values).success;
        if(!isValid) {
            toast.error("Formulier is niet correct");
            return;
        }
        updateMutation.mutate(values);
    }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
        <AlertDialogContent className='max-h-[90dvh] overflow-auto'>
            <AlertDialogHeader>
                <AlertDialogTitle>Contact wijzigen</AlertDialogTitle>
                <AlertDialogDescription>Vul de velden in om een contact te wijzigen</AlertDialogDescription>
            </AlertDialogHeader>
            <div className='flex flex-col gap-4'>
                <div>
                    <Label required>Naam</Label>
                    <Input {...form.register("contact_name")} />
                </div>
                <div>
                    <Label>Bedrijfsnaam</Label>
                    <Input {...form.register("company_name")} />
                </div>
                <div>
                    <Label>Email</Label>
                    <Input {...form.register("email")} />
                </div>
                <div>
                    <Label>Telefoon</Label>
                    <Input {...form.register("telephone")} />
                </div>
                <div>
                    <Label>Contact via</Label>
                    <Controller 
                        control={form.control}
                        name="contact_via"
                        render={({ field }) => (
                            <MultiSelect 
                                selectedOptions={field.value || []}
                                onValueChange={(v)=>{field.onChange(v)}}
                                options={[
                                    { value: "email", label: "Email" },
                                    { value: "telephone", label: "Telefoon" },
                                    { value: "whatsapp", label: "WhatsApp" },
                                    { value: "other", label: "Andere" },
                                ]}
                                placeholder='Contact via...'
                            />
                        )}
                    />
                </div>
                <div>
                    <Label>Notitie</Label>
                    <Textarea {...form.register("note")} />
                </div>
                <div>
                    <Label>Betrouwbaarheid</Label>
                    <Controller
                        control={form.control}
                        name="reliability"
                        render={({ field }) => (
                            <div className='flex items-center gap-2'>
                                <p className='text-xs font-medium bg-muted px-2 py-1 rounded-sm'>{field.value || 0}/5</p>
                                <Slider 
                                    value={field.value ? [field.value] : undefined}
                                    onValueChange={(v)=>{ field.onChange(v[0]) }}
                                    min={0}
                                    max={5}
                                    step={1}
                                />
                            </div>
                        )}
                    />
                </div>
                <div>
                    <Label>Kwaliteit spelers</Label>
                    <Controller
                        control={form.control}
                        name="quality_players"
                        render={({ field }) => (
                            <div className='flex items-center gap-2'>
                                <p className='text-xs font-medium bg-muted px-2 py-1 rounded-sm'>{field.value || 0}/5</p>
                                <Slider 
                                    value={field.value ? [field.value] : undefined}
                                    onValueChange={(v)=>{ field.onChange(v[0]) }}
                                    min={0}
                                    max={5}
                                    step={1}
                                />
                            </div>
                        )}
                    />
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <Button
                    disabled={!isFormValid || updateMutation.isLoading}
                    onClick={()=>{handleSubmit()}}
                >
                    {updateMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                    Opslaan
                </Button>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default UpdateContact